import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface DeviceLifecycleSectionProps {
  apiBaseUrl: string;
}

interface SeriesPoint {
  date: string;
  formattedDate: string;
  __versions?: FirmwareVersionsByModel;
  [key: string]: unknown;
}

type FirmwareVersionsByModel = Record<string, Record<string, number>>;

interface Series {
  dailyData: SeriesPoint[];
  monthlyData: SeriesPoint[];
  yearlyData: SeriesPoint[];
}

interface ModelInventory {
  model: string;
  flashed: number;
  setup: number;
  registered: number;
  flashedNotSetup: number;
  setupNotRegistered: number;
}

interface LifecycleMetrics {
  totalDevices: number;
  activity: Series;
  flashByModel: Series & { models: string[] };
  funnel: Series;
  inventory: {
    overall: {
      flashed: number;
      setup: number;
      registered: number;
      flashedNotSetup: number;
      setupNotRegistered: number;
    };
    models: ModelInventory[];
  };
}

interface HistoryPoint {
  date: string;
  formattedDate: string;
  totalDevices: number;
  flashed: number;
  setup: number;
  registered: number;
  flashedNotSetup: number;
  setupNotRegistered: number;
  models: ModelInventory[];
}

// A daily snapshot of how much of the fleet is actually communicating, taken
// from the long-running device_activity collection.
interface FleetPoint {
  date: string;
  activeFleet: number; // online + active = checked in within the last 30 days
  online: number; // checked in within the last 3 days
  total: number;
}

type TimeUnit = 'day' | 'month' | 'year';
interface TimeRange {
  unit: TimeUnit;
  value: number;
  label: string;
}

const TIME_RANGES: TimeRange[] = [
  { unit: 'day', value: 7, label: '1 Week' },
  { unit: 'day', value: 30, label: '1 Month' },
  { unit: 'day', value: 90, label: '3 Months' },
  { unit: 'month', value: 6, label: '6 Months' },
  { unit: 'month', value: 12, label: '1 Year' },
  { unit: 'year', value: 0, label: 'All Time' },
];

// Stage colors reused across the section.
const COLOR_FLASHED = '#6f42c1'; // purple  - factory flashed / in inventory
const COLOR_SETUP = '#4e73df'; // blue    - customer setup / first connection
const COLOR_REGISTERED = '#1cc88a'; // green   - registered to account

const MODEL_PALETTE = [
  '#4e73df', '#1cc88a', '#f6c23e', '#e74a3b', '#36b9cc',
  '#6f42c1', '#fd7e14', '#20c997', '#6610f2', '#858796',
];

const displayFirmwareVersion = (version: string) => {
  const normalized = String(version || '').trim().replace(/^v/i, '');
  return normalized ? `v${normalized}` : '';
};

const firmwareVersionSuffix = (versions?: Record<string, number>) => {
  const labels = Object.entries(versions || {})
    .filter(([, count]) => Number(count) > 0)
    .map(([version]) => displayFirmwareVersion(version))
    .filter(Boolean)
    .sort();
  return labels.length ? ` (${labels.join(', ')})` : '';
};

const activityTooltipFormatter = (value: number, name: string, entry: any) => {
  const dataKey = String(entry?.dataKey || name);
  const versions = entry?.payload?.__versions?.[dataKey] as Record<string, number> | undefined;
  return [`${Number(value).toLocaleString()} devices`, `${name}${firmwareVersionSuffix(versions)}`];
};

function sliceSeries(series: Series, range: TimeRange): SeriesPoint[] {
  let data =
    range.unit === 'day' ? series.dailyData : range.unit === 'month' ? series.monthlyData : series.yearlyData;
  data = [...(data || [])].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  if (range.value > 0 && data.length > range.value) {
    return data.slice(data.length - range.value);
  }
  return data;
}

const RangeButtons: React.FC<{ value: TimeRange; onChange: (r: TimeRange) => void }> = ({ value, onChange }) => (
  <div className="d-flex flex-wrap gap-1">
    {TIME_RANGES.map((range) => (
      <button
        key={range.label}
        className={`btn btn-sm ${value.label === range.label ? 'btn-primary' : 'btn-outline-primary'}`}
        onClick={() => onChange(range)}
      >
        {range.label}
      </button>
    ))}
  </div>
);

interface BarConfig {
  key: string;
  name: string;
  color: string;
}

// Generic time-series bar chart used by the three activity graphs.
const ActivityChart: React.FC<{
  title: string;
  subtitle: string;
  series: Series;
  bars: BarConfig[];
  stacked?: boolean;
}> = ({ title, subtitle, series, bars, stacked }) => {
  const [range, setRange] = useState<TimeRange>(TIME_RANGES[1]);
  const data = sliceSeries(series, range);
  const total = data.reduce(
    (sum, point) => sum + bars.reduce((s, b) => s + (Number(point[b.key]) || 0), 0),
    0
  );
  const isDay = range.unit === 'day';

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">{title}</h5>
        <div className="text-muted small">{subtitle}</div>
      </div>
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <RangeButtons value={range} onChange={setRange} />
          <span className="badge bg-light text-dark">{total.toLocaleString()} in period</span>
        </div>
        {total === 0 ? (
          <div className="alert alert-info text-center py-4 mb-0">No activity in the selected period.</div>
        ) : (
          <div style={{ height: '320px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 5, right: 30, left: 10, bottom: isDay ? 60 : 30 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="formattedDate"
                  angle={isDay ? -45 : 0}
                  textAnchor={isDay ? 'end' : 'middle'}
                  height={isDay ? 60 : 30}
                  tickMargin={isDay ? 15 : 8}
                  interval={isDay && data.length > 31 ? Math.floor(data.length / 15) : 0}
                />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={activityTooltipFormatter} />
                {bars.length > 1 && <Legend />}
                {bars.map((b) => (
                  <Bar
                    key={b.key}
                    dataKey={b.key}
                    name={b.name}
                    fill={b.color}
                    stackId={stacked ? 'activity' : undefined}
                    radius={stacked ? undefined : [4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

// Spike-free view of how much of the fleet is actually communicating over time,
// driven by the daily device_activity snapshots (not derived from first-contact
// dates, which clump on factory batches).
const FLEET_RANGES: { label: string; days: number }[] = [
  { label: '3 Months', days: 90 },
  { label: '6 Months', days: 180 },
  { label: '1 Year', days: 365 },
  { label: 'All Time', days: Infinity },
];

const ActiveFleetChart: React.FC<{ data: FleetPoint[] }> = ({ data }) => {
  const [range, setRange] = useState<{ label: string; days: number }>(FLEET_RANGES[2]);

  const filtered = useMemo(() => {
    const sorted = [...data].sort((a, b) => String(a.date).localeCompare(String(b.date)));
    if (!Number.isFinite(range.days)) return sorted;
    const cutoff = Date.now() - range.days * 86400000;
    return sorted.filter((p) => new Date(p.date).getTime() >= cutoff);
  }, [data, range]);

  const short = range.days <= 90;
  const fmtTick = (value: string) =>
    new Date(value).toLocaleDateString('en-US', short ? { month: 'short', day: 'numeric' } : { month: 'short', year: '2-digit' });
  const fmtFull = (value: string) =>
    new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const latest = filtered.length ? filtered[filtered.length - 1] : null;

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">Active Devices Over Time</h5>
        <div className="text-muted small">
          Devices communicating with the server (checked in within 30 days), from daily snapshots
        </div>
      </div>
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <div className="d-flex flex-wrap gap-1">
            {FLEET_RANGES.map((r) => (
              <button
                key={r.label}
                className={`btn btn-sm ${range.label === r.label ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setRange(r)}
              >
                {r.label}
              </button>
            ))}
          </div>
          {latest && (
            <span className="badge bg-light text-dark">{latest.activeFleet.toLocaleString()} active now</span>
          )}
        </div>
        {filtered.length === 0 ? (
          <div className="alert alert-info text-center py-4 mb-0">No activity snapshots for the selected period.</div>
        ) : (
          <div style={{ height: '320px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={filtered} margin={{ top: 5, right: 30, left: 10, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={fmtTick}
                  minTickGap={24}
                  interval={filtered.length > 24 ? Math.floor(filtered.length / 12) : 0}
                />
                <YAxis allowDecimals={false} />
                <Tooltip labelFormatter={fmtFull} formatter={(value: number, name: string) => [`${value.toLocaleString()} devices`, name]} />
                <Legend />
                <Line type="monotone" dataKey="activeFleet" name="Active (checked in \u2264 30 days)" stroke={COLOR_SETUP} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="online" name="Online (\u2264 3 days)" stroke={COLOR_REGISTERED} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

// The three mutually-exclusive lifecycle stages (each device is in exactly one).
const STAGE_SEGMENTS: BarConfig[] = [
  { key: 'flashedNotSetup', name: 'Flashed (inventory)', color: COLOR_FLASHED },
  { key: 'setupNotRegistered', name: 'Active', color: COLOR_SETUP },
  { key: 'registered', name: 'Registered', color: COLOR_REGISTERED },
];

// Stacked-area funnel over time, with a count / percent toggle.
const FunnelChart: React.FC<{ series: Series }> = ({ series }) => {
  const [range, setRange] = useState<TimeRange>(TIME_RANGES[4]);
  const [mode, setMode] = useState<'count' | 'percent'>('count');
  const raw = sliceSeries(series, range);

  const data = useMemo(() => {
    if (mode === 'count') return raw;
    return raw.map((point) => {
      const total = STAGE_SEGMENTS.reduce((s, seg) => s + (Number(point[seg.key]) || 0), 0) || 1;
      const scaled: SeriesPoint = { date: point.date, formattedDate: point.formattedDate };
      STAGE_SEGMENTS.forEach((seg) => {
        scaled[seg.key] = Math.round(((Number(point[seg.key]) || 0) / total) * 1000) / 10;
      });
      return scaled;
    });
  }, [raw, mode]);

  const isDay = range.unit === 'day';
  const hasData = raw.some((p) => (Number(p.total) || 0) > 0);

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">Lifecycle Funnel</h5>
        <div className="text-muted small">
          Flashed (inventory) &rarr; Active (in use, contacted server) &rarr; Registered (on an account), over time
        </div>
      </div>
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <RangeButtons value={range} onChange={setRange} />
          <div className="btn-group btn-group-sm" role="group">
            <button
              className={`btn ${mode === 'count' ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => setMode('count')}
            >
              Count
            </button>
            <button
              className={`btn ${mode === 'percent' ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => setMode('percent')}
            >
              Percent
            </button>
          </div>
        </div>
        {!hasData ? (
          <div className="alert alert-info text-center py-4 mb-0">No lifecycle data for the selected period.</div>
        ) : (
          <div style={{ height: '340px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 5, right: 30, left: 10, bottom: isDay ? 60 : 30 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="formattedDate"
                  angle={isDay ? -45 : 0}
                  textAnchor={isDay ? 'end' : 'middle'}
                  height={isDay ? 60 : 30}
                  tickMargin={isDay ? 15 : 8}
                  interval={isDay && data.length > 31 ? Math.floor(data.length / 15) : 0}
                />
                <YAxis
                  allowDecimals={mode === 'percent'}
                  domain={mode === 'percent' ? [0, 100] : [0, 'auto']}
                  tickFormatter={(v: number) => (mode === 'percent' ? `${v}%` : `${v}`)}
                />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    mode === 'percent' ? `${value}%` : `${value} devices`,
                    name,
                  ]}
                />
                <Legend />
                {STAGE_SEGMENTS.map((seg) => (
                  <Area
                    key={seg.key}
                    type="monotone"
                    dataKey={seg.key}
                    name={seg.name}
                    stackId="funnel"
                    stroke={seg.color}
                    fill={seg.color}
                    fillOpacity={0.75}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

const INVENTORY_SEGMENTS = STAGE_SEGMENTS;

// Current per-model breakdown, plus inventory-over-time from daily snapshots.
const InventoryChart: React.FC<{ models: ModelInventory[]; history: HistoryPoint[] }> = ({ models, history }) => {
  const [view, setView] = useState<'current' | 'history'>('current');
  const hasCurrent = models.some((m) => m.flashed > 0);
  const hasHistory = history.length > 0;

  const chartHeight = Math.max(260, models.length * 46);

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">Inventory by Model</h5>
        <div className="text-muted small">
          Each model broken down by lifecycle stage. &ldquo;Flashed&rdquo; = factory-flashed but not yet set up (inventory on hand).
        </div>
      </div>
      <div className="card-body">
        <div className="d-flex justify-content-end mb-3">
          <div className="btn-group btn-group-sm" role="group">
            <button
              className={`btn ${view === 'current' ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => setView('current')}
            >
              Current
            </button>
            <button
              className={`btn ${view === 'history' ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => setView('history')}
              disabled={!hasHistory}
              title={hasHistory ? undefined : 'No daily snapshots collected yet'}
            >
              Over time
            </button>
          </div>
        </div>

        {view === 'current' ? (
          !hasCurrent ? (
            <div className="alert alert-info text-center py-4 mb-0">No flashed devices recorded yet.</div>
          ) : (
            <div style={{ height: `${chartHeight}px` }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={models} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="model" width={120} />
                  <Tooltip formatter={(value: number, name: string) => [`${value} devices`, name]} />
                  <Legend />
                  {INVENTORY_SEGMENTS.map((seg) => (
                    <Bar key={seg.key} dataKey={seg.key} name={seg.name} fill={seg.color} stackId="inv" />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )
        ) : (
          <div style={{ height: '320px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history} margin={{ top: 5, right: 30, left: 10, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="formattedDate" />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={(value: number, name: string) => [`${value} devices`, name]} />
                <Legend />
                <Line type="monotone" dataKey="flashedNotSetup" name="Flashed (inventory)" stroke={COLOR_FLASHED} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="setupNotRegistered" name="Active" stroke={COLOR_SETUP} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="registered" name="Registered" stroke={COLOR_REGISTERED} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="flashed" name="Flashed (all-time)" stroke="#858796" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

const SummaryCard: React.FC<{ label: string; value: number; color?: string }> = ({ label, value, color }) => (
  <div className="col-6 col-md">
    <div className="card bg-light h-100">
      <div className="card-body py-2">
        <div className="small text-muted d-flex align-items-center">
          {color && (
            <span
              style={{
                display: 'inline-block',
                width: '0.75rem',
                height: '0.75rem',
                borderRadius: '0.2rem',
                backgroundColor: color,
                marginRight: '0.4rem',
              }}
            />
          )}
          {label}
        </div>
        <div className="h5 mb-0">{value.toLocaleString()}</div>
      </div>
    </div>
  </div>
);

const DeviceLifecycleSection: React.FC<DeviceLifecycleSectionProps> = ({ apiBaseUrl }) => {
  const [metrics, setMetrics] = useState<LifecycleMetrics | null>(null);
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [activeFleet, setActiveFleet] = useState<FleetPoint[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [metricsRes, historyRes, fleetRes] = await Promise.all([
          axios.get(`${apiBaseUrl}/devices/lifecycle/metrics`, { withCredentials: true }),
          axios.get(`${apiBaseUrl}/devices/lifecycle/inventory-history`, { withCredentials: true }),
          axios.get(`${apiBaseUrl}/devices/activity-metrics/history?days=1000`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setMetrics(metricsRes.data);
        setHistory(historyRes.data?.history || []);
        setActiveFleet(
          ((fleetRes.data?.history as Array<Record<string, unknown>>) || []).map((d) => ({
            date: String(d.date),
            activeFleet: (Number(d.onlineDevices) || 0) + (Number(d.activeDevices) || 0),
            online: Number(d.onlineDevices) || 0,
            total: Number(d.totalDevices) || 0,
          }))
        );
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching device lifecycle metrics:', err);
        setError('Failed to fetch device lifecycle metrics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl]);

  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error && !metrics) {
    return <div className="alert alert-danger">{error}</div>;
  }

  if (!metrics) {
    return <div className="alert alert-warning">No device lifecycle data available</div>;
  }

  const flashBars: BarConfig[] = metrics.flashByModel.models.map((model, i) => ({
    key: model,
    name: model,
    color: MODEL_PALETTE[i % MODEL_PALETTE.length],
  }));

  const { overall } = metrics.inventory;

  return (
    <div>
      {/* Summary: the three mutually-exclusive stages sum to the total */}
      <div className="row g-2 mb-4">
        <SummaryCard label="Total devices" value={metrics.totalDevices} />
        <SummaryCard label="Flashed (inventory)" value={overall.flashedNotSetup} color={COLOR_FLASHED} />
        <SummaryCard label="Active" value={overall.setupNotRegistered} color={COLOR_SETUP} />
        <SummaryCard label="Registered" value={overall.registered} color={COLOR_REGISTERED} />
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <ActivityChart
            title="Factory Flash Activity"
            subtitle="New devices flashed per period, by model"
            series={metrics.flashByModel}
            bars={flashBars}
            stacked
          />
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <ActiveFleetChart data={activeFleet} />
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <ActivityChart
            title="Account Registration Activity"
            subtitle="Devices registered to a customer account"
            series={metrics.activity}
            bars={[{ key: 'registered', name: 'Registered', color: COLOR_REGISTERED }]}
          />
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <FunnelChart series={metrics.funnel} />
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <InventoryChart models={metrics.inventory.models} history={history} />
        </div>
      </div>
    </div>
  );
};

export default DeviceLifecycleSection;
